import { ShieldCheck, Lock, Gem, Timer } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { STATS, whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const VALUES = [
  { icon: ShieldCheck, title: "Originality", text: "Every document begins as a blank page. No templates, no recycling, no shortcuts — and a plagiarism check before anything leaves our desk." },
  { icon: Lock, title: "Confidentiality", text: "Your name, institution, and materials are never shared, published, or reused. What you send us stays between us." },
  { icon: Gem, title: "Quality", text: "Work is drafted by subject specialists and reviewed by a second pair of eyes before delivery. Good enough is not our standard." },
  { icon: Timer, title: "Timely delivery", text: "Deadlines in academia are unforgiving, so ours are non-negotiable. Milestone schedules keep every project visibly on track." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us | MBA Project & Thesis Writing Help"
        description="An independent academic writing team with advanced degrees across business, law, and technical disciplines. Built on originality, confidentiality, quality, and timely delivery."
      />
      <PageHero
        eyebrow="About us"
        lines={["Serious writing,", "for serious students."]}
        sub="We exist for one reason: to help capable students present their research at the standard their effort deserves."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-stats">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm">
              <p className="font-display font-extrabold text-3xl md:text-4xl text-[#2563EB] leading-none">{s.value}</p>
              <p className="mt-2 text-xs md:text-sm text-slate-500 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <Reveal>
          <Eyebrow>Our story</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
            It started with one observation.
          </h2>
          <p className="mt-5 text-sm md:text-base text-slate-600 leading-relaxed">
            Talented students were losing marks not because their ideas were weak, but because academic writing is a craft nobody formally taught them. Working professionals in MBA programmes had insight but no time. International students had research but wrestled with formal English. Doctoral scholars had data but struggled to shape it into an argument.
          </p>
          <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
            We built this service to close that gap — pairing students with writers who know both the subject matter and the unwritten rules of academic assessment. Today we support students at universities across India, Europe, North America, and the Middle East, from first-semester assignments to final doctoral submissions.
          </p>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
            <img
              src="https://images.pexels.com/photos/8374275/pexels-photo-8374275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Writer reviewing and highlighting academic notes at a desk"
              className="w-full h-64 object-cover"
            />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <Eyebrow>Our team</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
            Specialists, not generalists.
          </h2>
          <p className="mt-5 text-sm md:text-base text-slate-600 leading-relaxed">
            Our writers hold advanced degrees across business, law, engineering, and the social sciences. Each project is matched to a writer with genuine background in the discipline — a finance thesis goes to someone fluent in finance, a law dissertation to someone trained in legal research.
          </p>
          <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
            Behind the writers sits a quality team that reviews structure, referencing, and originality on every delivery. We would rather take on less work than compromise the standard that keeps students coming back and referring their friends.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <Reveal>
          <Eyebrow>How we work</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight max-w-2xl">
            Simple, clear, and honest — from first message to final file.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm md:text-base text-slate-600 leading-relaxed" data-testid="how-we-work">
            <p>
              <strong className="text-slate-900 font-semibold">It starts with a conversation.</strong> You message us on WhatsApp with your topic, guidelines, and deadline. We ask a few questions — never assumptions — and come back with a clear plan and a fixed quote. No payment until you're happy with the plan.
            </p>
            <p>
              <strong className="text-slate-900 font-semibold">Then the right people take over.</strong> Your work goes to a writer from your own discipline, backed by a quality reviewer who checks structure, referencing, and originality. You get updates at every milestone, so you always know where things stand.
            </p>
            <p>
              <strong className="text-slate-900 font-semibold">And you stay in control.</strong> You review drafts as they arrive and ask for changes freely within scope. The final file comes formatted to your university's rules, with a plagiarism report attached — ready to submit.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#F5F8FF] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <Eyebrow>Our values</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              Four principles. Zero exceptions.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07} className="bg-white border border-slate-200 rounded-2xl p-8 h-full" data-testid={`value-card-${i}`}>
                <v.icon className="h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-bold text-lg tracking-tight">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal className="text-center sm:text-left">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter">
            Want to know if we're the right fit?
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl">
            Message us with your requirement. If it's not something we can do exceptionally well, we'll say so.
          </p>
          <a
            href={whatsappWithText("Hi, I'd like to know more about your team and how you work.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-whatsapp-cta"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#1E2A78] px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[#2563EB] hover:-translate-y-0.5"
          >
            <WhatsAppIcon className="h-5 w-5" /> Start a Conversation
          </a>
        </Reveal>
      </section>
    </div>
  );
}
